/**
 * Net-cost field detection and stripping.
 *
 * Item net cost (e.g. `averageNetCost`) is at least as sensitive as gross
 * profit: exposing what the distributor actually pays after rebates and
 * discounts leaks negotiating position. It is therefore gated by its own
 * RBAC action, enforced the same way as gross profit — every outgoing
 * response is stripped of net-cost keys for users without the action, and
 * the UI renders a lock if a value somehow reaches it.
 */
import { stripFieldsMatching } from './gross-profit';

/**
 * Canonical RBAC action that gates net-cost visibility everywhere.
 * Hard-coded here to avoid a cycle with `RbacActions` (this util is imported
 * by code that builds those structures).
 */
export const NET_COST_VIEW_PERMISSION = 'items:net_cost:view';

/**
 * "netcost" / "net_cost" anywhere in the key, case-insensitive. Catches
 * netCost, NET_COST, averageNetCost, avgNetCostTrend30, item_net_cost.
 *
 * Same conservative bias as the gross-profit detector: a false positive
 * strips a non-net-cost field (annoying), a false negative leaks net cost
 * (security issue).
 */
const NET_COST_SUBSTRING = /net_?cost/i;

/** Returns true when the given key represents a net-cost value. */
export function isNetCostFieldName(key: string): boolean {
	if (key == null || key === '') {
		return false;
	}
	return NET_COST_SUBSTRING.test(key);
}

/**
 * Recursively walk `value` and delete any property whose key is a net-cost
 * field. Same walk semantics as `stripGpFields` (in-place mutation, cycle
 * tracking, built-ins skipped).
 */
export function stripNetCostFields<T>(value: T): T {
	return stripFieldsMatching(value, isNetCostFieldName);
}
